import type { ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { t } from '@lingui/core/macro'
import { useMutation } from '@tanstack/react-query'

import SvgCheck from '@jotta/ui/SvgCheck'
import SvgCopy from '@jotta/ui/SvgCopy'
import { cn } from '@jotta/utils/css'

import { showToast } from '../../Toasts/ToastProvider'

export function CopyButton({
  value,
  buttonText,
  className,
}: {
  value: string
  buttonText?: (copied: boolean) => ReactNode
  className?: string
}) {
  const { isSuccess, mutate, reset } = useMutation({
    mutationFn: () => navigator.clipboard.writeText(value),
    throwOnError: false,
    onSuccess: () => {
      setTimeout(() => {
        reset()
      }, 1500)
    },
    onError: error => {
      showToast({
        message: t`Copy to clipboard failed`,
        hasCloseButton: true,
      })
      reportError(error)
    },
  })

  return (
    <button
      onClick={() => mutate()}
      aria-label={isSuccess ? t`Copied` : t`Copy`}
      className={cn(
        'btn btn-elevated bg-on-primary-container text-on-primary max-sm:w-10 max-sm:p-0',
        className,
        { 'w-10 p-0': !buttonText },
      )}
    >
      <AnimatePresence initial={false} mode="wait">
        {isSuccess ? (
          <motion.span
            key="checkIcon"
            initial={{ y: 0 }}
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <SvgCheck aria-hidden="true" />
          </motion.span>
        ) : (
          <SvgCopy aria-hidden="true" />
        )}
      </AnimatePresence>

      {buttonText && (
        <span className="hidden sm:block">{buttonText(isSuccess)}</span>
      )}
    </button>
  )
}
